import React from "react";
import { NavbarDemo } from "./NavBar";
import {
  Calendar,
  Clock,
  AlertCircle,
  DollarSign,
  Mail,
  XCircle,
} from "lucide-react";

const CancellationPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <NavbarDemo />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-6">
            <Calendar className="h-8 w-8 text-orange-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Cancellation Policy
          </h1>
          <p className="text-gray-600">
            Last updated:{" "}
            {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Overview
            </h2>
            <p className="text-gray-700 leading-relaxed">
              BookaTherapy understands that sometimes you need to cancel or
              reschedule appointments. This Cancellation Policy outlines the
              terms and conditions for canceling or modifying appointments
              booked through our platform. By booking an appointment, you agree
              to the terms outlined in this policy.
            </p>
          </section>

          {/* Cancellation Timeframes */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Clock className="h-6 w-6 text-orange-600" />
              <h2 className="text-2xl font-semibold text-gray-900">
                Cancellation Timeframes
              </h2>
            </div>

            <div className="space-y-6">
              <div className="border-l-4 border-green-500 pl-4 py-2">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  More than 24 Hours Notice
                </h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  If you cancel or reschedule your appointment more than 24
                  hours before the scheduled time:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>
                    You will receive a <strong>full refund</strong> of any
                    payment made
                  </li>
                  <li>No cancellation fees will be charged</li>
                  <li>You can reschedule to any available time slot</li>
                  <li>Refunds will be processed within 5-7 business days</li>
                </ul>
              </div>

              <div className="border-l-4 border-yellow-500 pl-4 py-2">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Between 12-24 Hours Notice
                </h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  If you cancel or reschedule your appointment between 12-24
                  hours before the scheduled time:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>
                    You will receive a <strong>50% refund</strong> of the
                    appointment fee
                  </li>
                  <li>
                    A cancellation fee equal to 50% of the appointment cost will
                    be retained
                  </li>
                  <li>
                    You can reschedule once without additional fees within 30
                    days
                  </li>
                  <li>Refunds will be processed within 5-7 business days</li>
                </ul>
              </div>

              <div className="border-l-4 border-red-500 pl-4 py-2">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Less than 12 Hours Notice or No-Show
                </h3>
                <p className="text-gray-700 leading-relaxed mb-2">
                  If you cancel less than 12 hours before the appointment or
                  fail to show up:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>
                    <strong>No refund</strong> will be provided
                  </li>
                  <li>The full appointment fee will be charged</li>
                  <li>
                    You may be required to pay a deposit for future bookings
                  </li>
                  <li>Repeated no-shows may result in account restrictions</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How to Cancel */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              How to Cancel or Reschedule
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Online Cancellation
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  You can cancel or reschedule appointments directly through
                  your BookaTherapy account:
                </p>
                <ol className="list-decimal list-inside space-y-1 text-gray-700 ml-4 mt-2">
                  <li>Log in to your account</li>
                  <li>Navigate to "My Appointments" or "Dashboard"</li>
                  <li>
                    Select the appointment you wish to cancel or reschedule
                  </li>
                  <li>Click "Cancel" or "Reschedule" and follow the prompts</li>
                  <li>Confirm your cancellation or select a new time slot</li>
                </ol>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Phone Cancellation
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  You can also cancel by calling our support line at{" "}
                  <a
                    href="tel:18008437279"
                    className="text-blue-600 hover:underline">
                    +1 (780) 267-7551
                  </a>
                  . Please have your appointment confirmation number ready.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Email Cancellation
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Send an email to{" "}
                  <a
                    href="mailto:info@bookatherapy.com"
                    className="text-blue-600 hover:underline">
                    info@bookatherapy.com
                  </a>{" "}
                  with your appointment details and cancellation request.
                  Cancellation time is determined by when we receive your email.
                </p>
              </div>
            </div>
          </section>

          {/* Refund Processing */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="h-6 w-6 text-green-600" />
              <h2 className="text-2xl font-semibold text-gray-900">
                Refund Processing
              </h2>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Processing Time
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Refunds will be processed to your original payment method
                  within 5-7 business days from the date of cancellation.
                  Processing times may vary depending on your bank or credit
                  card provider.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Refund Method
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Refunds will be issued to the same payment method used for the
                  original booking. If your payment method is no longer valid,
                  please contact our support team to arrange an alternative
                  refund method.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Processing Fees
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Any processing fees charged by payment processors may be
                  deducted from your refund amount. These fees are typically
                  minimal and will be clearly communicated during the refund
                  process.
                </p>
              </div>
            </div>
          </section>

          {/* Emergency Cancellations */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Emergency Cancellations
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
              <p className="text-gray-700 leading-relaxed mb-4">
                We understand that emergencies happen. In cases of genuine
                emergencies (medical emergencies, family emergencies, natural
                disasters, etc.), we may waive cancellation fees at our
                discretion.
              </p>
              <p className="text-gray-700 leading-relaxed">
                To request an emergency cancellation waiver, please contact our
                support team as soon as possible with documentation of the
                emergency. Each case will be reviewed individually.
              </p>
            </div>
          </section>

          {/* Therapist Cancellations */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Therapist Cancellations
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If your therapist needs to cancel your appointment:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>
                You will receive a <strong>full refund</strong> or the option to
                reschedule at no cost
              </li>
              <li>
                We will notify you as soon as possible via email and/or phone
              </li>
              <li>
                We will help you find an alternative therapist or time slot if
                desired
              </li>
              <li>No cancellation fees will apply to you</li>
            </ul>
          </section>

          {/* Multiple Cancellations */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-orange-600" />
              <h2 className="text-2xl font-semibold text-gray-900">
                Multiple Cancellations
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              To ensure fairness for both clients and therapists, repeated
              cancellations may result in:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Requirement of a deposit for future bookings</li>
              <li>Restricted access to certain booking features</li>
              <li>Account review and potential restrictions</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              We reserve the right to review accounts with excessive
              cancellation rates and may contact you to discuss alternative
              arrangements.
            </p>
          </section>

          {/* Package and Subscription Cancellations */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Package and Subscription Cancellations
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Service Packages
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  If you have purchased a package of multiple sessions,
                  individual session cancellations follow the same policy
                  outlined above. Unused sessions in a package may be refunded
                  on a prorated basis if the entire package is canceled with at
                  least 48 hours notice.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Subscriptions
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Subscription cancellations can be made at any time through
                  your account settings. Cancellations will take effect at the
                  end of your current billing period. No refunds will be
                  provided for the current billing period.
                </p>
              </div>
            </div>
          </section>

          {/* Gift Card Bookings */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Gift Card Bookings
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Appointments booked using gift cards follow the same cancellation
              policy. Refunds for canceled appointments will be issued as credit
              back to the gift card balance, not as cash refunds.
            </p>
          </section>

          {/* Insurance and Third-Party Payments */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Insurance and Third-Party Payments
            </h2>
            <p className="text-gray-700 leading-relaxed">
              If your appointment is covered by insurance or paid by a third
              party, cancellation policies may vary. Please check with your
              insurance provider or the paying party for specific terms.
              BookaTherapy will follow the terms agreed upon at the time of
              booking.
            </p>
          </section>

          {/* Policy Changes */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Policy Changes
            </h2>
            <p className="text-gray-700 leading-relaxed">
              BookaTherapy reserves the right to modify this Cancellation Policy
              at any time. Changes will be effective immediately upon posting on
              our website. The policy in effect at the time of your booking will
              apply to that booking.
            </p>
          </section>

          {/* Disputes */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Disputes and Appeals
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you believe a cancellation fee was charged incorrectly or wish
              to dispute a cancellation decision, please contact our support
              team:
            </p>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700">
                <strong>Email:</strong>{" "}
                <a
                  href="mailto:info@bookatherapy.com"
                  className="text-blue-600 hover:underline">
                  info@bookatherapy.com
                </a>
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Phone:</strong>{" "}
                <a
                  href="tel:18008437279"
                  className="text-blue-600 hover:underline">
                  +1 (780) 267-7551
                </a>
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Response Time:</strong> We aim to respond to disputes
                within 2-3 business days.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section className="bg-orange-50 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="h-6 w-6 text-orange-600" />
              <h2 className="text-2xl font-semibold text-gray-900">
                Questions?
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about our Cancellation Policy, please
              don't hesitate to contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <p>
                <strong>Email:</strong>{" "}
                <a
                  href="mailto:info@bookatherapy.com"
                  className="text-blue-600 hover:underline">
                  info@bookatherapy.com
                </a>
              </p>
              <p>
                <strong>Phone:</strong>{" "}
                <a
                  href="tel:18008437279"
                  className="text-blue-600 hover:underline">
                  +1 (780) 267-7551
                </a>
              </p>
              <p>
                <strong>Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM EST
              </p>
            </div>
          </section>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            By booking an appointment through BookaTherapy, you acknowledge that
            you have read and understood this Cancellation Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CancellationPolicy;
